import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar, TrendingUp, Heart, Target, Flame, BookOpen } from 'lucide-react';

// Daily rotating quotes with the same powerful, reflective vibe
const REFLECTION_QUOTES = [
  "The ones who learn from their past don't just move forward, they leap beyond what they thought was possible.",
  "Your history is not your destiny. It's your teacher.",
  "Look back to learn. Look forward to grow. Look inward to transform.",
  "Every entry is evidence that you showed up for yourself.",
  "The patterns you track today become the wisdom you carry tomorrow.",
  "Reflection isn't dwelling—it's building the blueprint for who you're becoming.",
  "Small daily choices compound into extraordinary lives.",
  "The person who reviews their journey travels twice.",
  "Your past self left breadcrumbs. Follow them to find your breakthrough.",
  "Growth isn't loud. It's the quiet accumulation of days like these.",
  "What you measure, you master. What you reflect on, you refine.",
  "The distance between who you are and who you want to be is filled with days like today.",
  "Champions are made in the margins—in the moments others skip.",
  "Your future self is watching. Make them proud.",
  "Consistency is the quiet rebellion against mediocrity.",
  "Every page turned here is a page written in your story.",
  "The compound effect of intention is unstoppable.",
  "You don't rise to your goals. You fall to your systems.",
  "The path to extraordinary is paved with ordinary days done well.",
  "What feels like slow progress is actually deep roots forming.",
  "Your commitment to yourself is the most important promise you'll ever keep.",
  "The only competition that matters is who you were yesterday.",
  "Discipline is just love with a long-term view.",
  "Every habit tracked is a vote for the person you're becoming.",
  "The best time to plant a tree was 20 years ago. The second best time is today.",
  "Success leaves clues. Your reflections are the map.",
  "The gap between your dreams and reality is called discipline.",
  "What you do in private determines what you become in public.",
  "Small hinges swing big doors.",
  "Your rituals become your results.",
  "The magic you're looking for is in the work you're avoiding.",
];

const getDailyReflectionQuote = () => {
  const today = new Date();
  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
  return REFLECTION_QUOTES[dayOfYear % REFLECTION_QUOTES.length];
};

const sage = '#8a9a8a';
const sageBg = 'rgba(138, 154, 138, 0.1)';

export const Reflections = ({ allData, settings, calculateCompletionPercentage }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Get all days in the current month
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateKey = date.toISOString().split('T')[0];
      const hasData = allData[dateKey] !== undefined;
      const completion = hasData ? calculateCompletionPercentage(allData[dateKey]) : 0;
      
      days.push({
        day,
        date,
        dateKey,
        hasData,
        completion,
        data: allData[dateKey] || null
      });
    }
    
    return days;
  }, [currentMonth, allData, calculateCompletionPercentage]);

  // Calculate insights from all data
  const insights = useMemo(() => {
    const entries = Object.entries(allData);
    if (entries.length === 0) return null;

    let totalDays = entries.length;
    let totalCompletion = 0;
    let habitCounts = {
      gym: 0,
      nature: 0,
      practicedSkill: 0,
      moneyMove: 0,
      decluttered: 0,
      connectedWithSomeone: 0,
      brushedTeethAM: 0,
      brushedTeethPM: 0,
      skinCareAM: 0,
      skinCarePM: 0,
    };
    let totalGratitudeEntries = 0;
    let avgDayRating = 0;
    let ratingCount = 0;

    entries.forEach(([key, data]) => {
      totalCompletion += calculateCompletionPercentage(data);
      
      Object.keys(habitCounts).forEach(habit => {
        if (data[habit]) habitCounts[habit]++;
      });
      
      if (data.gratitude?.length > 0) totalGratitudeEntries++;
      if (data.dayRating) {
        avgDayRating += data.dayRating;
        ratingCount++;
      }
    });

    // Find most consistent habit
    const sortedHabits = Object.entries(habitCounts)
      .sort(([, a], [, b]) => b - a);
    
    const mostConsistent = sortedHabits[0];
    const leastConsistent = sortedHabits[sortedHabits.length - 1];

    const habitLabels = {
      gym: 'Working out',
      nature: 'Going outside',
      practicedSkill: 'Practicing a skill',
      moneyMove: 'Making money moves',
      decluttered: 'Decluttering',
      connectedWithSomeone: 'Connecting with others',
      brushedTeethAM: 'Morning teeth brushing',
      brushedTeethPM: 'Evening teeth brushing',
      skinCareAM: 'Morning skincare',
      skinCarePM: 'Evening skincare',
    };

    return {
      totalDays,
      avgCompletion: Math.round(totalCompletion / totalDays),
      mostConsistent: {
        habit: habitLabels[mostConsistent[0]] || mostConsistent[0],
        count: mostConsistent[1],
        percentage: Math.round((mostConsistent[1] / totalDays) * 100)
      },
      leastConsistent: {
        habit: habitLabels[leastConsistent[0]] || leastConsistent[0],
        count: leastConsistent[1],
        percentage: Math.round((leastConsistent[1] / totalDays) * 100)
      },
      gratitudeStreak: totalGratitudeEntries,
      avgDayRating: ratingCount > 0 ? (avgDayRating / ratingCount).toFixed(1) : 0
    };
  }, [allData, calculateCompletionPercentage]);

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
    setSelectedDate(null);
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const selectedDayData = selectedDate ? allData[selectedDate] : null;

  return (
    <div className="space-y-8" data-testid="reflections-page">
      {/* Inspirational Quote - Changes Daily */}
      <section className="text-center py-6">
        <BookOpen className="w-8 h-8 mx-auto mb-4" style={{ color: sage }} />
        <p className="text-lg text-white/50 italic leading-relaxed font-light max-w-md mx-auto">
          "{getDailyReflectionQuote()}"
        </p>
      </section>

      {/* Insights Section */}
      {insights && (
        <section className="glass-card p-6" data-testid="insights-section">
          <h3 className="text-sm text-white/30 mb-6 tracking-widest uppercase">Your Insights</h3>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="p-4 rounded-xl" style={{ backgroundColor: sageBg }}>
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4" style={{ color: sage }} />
                <span className="text-white/40 text-xs">Days Tracked</span>
              </div>
              <span className="text-2xl font-light" style={{ color: sage }}>{insights.totalDays}</span>
            </div>
            
            <div className="p-4 rounded-xl" style={{ backgroundColor: sageBg }}>
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4" style={{ color: sage }} />
                <span className="text-white/40 text-xs">Avg Completion</span>
              </div>
              <span className="text-2xl font-light" style={{ color: sage }}>{insights.avgCompletion}%</span>
            </div>
            
            <div className="p-4 rounded-xl" style={{ backgroundColor: sageBg }}>
              <div className="flex items-center gap-2 mb-2">
                <Heart className="w-4 h-4" style={{ color: sage }} />
                <span className="text-white/40 text-xs">Avg Day Rating</span>
              </div>
              <span className="text-2xl font-light" style={{ color: sage }}>{insights.avgDayRating}/10</span>
            </div>
            
            <div className="p-4 rounded-xl" style={{ backgroundColor: sageBg }}>
              <div className="flex items-center gap-2 mb-2">
                <Flame className="w-4 h-4" style={{ color: sage }} />
                <span className="text-white/40 text-xs">Gratitude Days</span>
              </div>
              <span className="text-2xl font-light" style={{ color: sage }}>{insights.gratitudeStreak}</span>
            </div>
          </div>

          <div className="space-y-3">
            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.05]">
              <p className="text-white/40 text-xs mb-1">Most Consistent</p>
              <p className="text-white/80 font-medium">{insights.mostConsistent.habit}</p>
              <p className="text-white/30 text-sm">{insights.mostConsistent.percentage}% of days</p>
            </div>
            
            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.05]">
              <p className="text-white/40 text-xs mb-1">Room for Growth</p>
              <p className="text-white/80 font-medium">{insights.leastConsistent.habit}</p>
              <p className="text-white/30 text-sm">{insights.leastConsistent.percentage}% of days</p>
            </div>
          </div>
        </section>
      )}

      {/* Calendar Section */}
      <section className="glass-card p-6" data-testid="calendar-section">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigateMonth(-1)}
            className="p-2 rounded-lg hover:bg-white/[0.05] transition-colors"
            data-testid="prev-month-btn"
          >
            <ChevronLeft className="w-5 h-5 text-white/50" />
          </button>
          
          <h3 className="text-white/80 font-medium">{formatMonthYear(currentMonth)}</h3>
          
          <button
            onClick={() => navigateMonth(1)}
            className="p-2 rounded-lg hover:bg-white/[0.05] transition-colors"
            data-testid="next-month-btn"
          >
            <ChevronRight className="w-5 h-5 text-white/50" />
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-white/30 text-xs py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((dayInfo, index) => (
            <button
              key={index}
              onClick={() => dayInfo?.hasData && setSelectedDate(dayInfo.dateKey)}
              disabled={!dayInfo?.hasData}
              className={`aspect-square rounded-lg flex flex-col items-center justify-center transition-all text-sm ${
                dayInfo === null
                  ? 'invisible'
                  : dayInfo.hasData
                    ? selectedDate === dayInfo.dateKey
                      ? 'ring-2'
                      : 'hover:bg-white/[0.05]'
                    : 'text-white/20 cursor-default'
              }`}
              style={{
                backgroundColor: dayInfo?.hasData 
                  ? `rgba(138, 154, 138, ${dayInfo.completion / 200})` 
                  : 'transparent',
                ringColor: sage
              }}
              data-testid={dayInfo ? `calendar-day-${dayInfo.day}` : undefined}
            >
              <span className={dayInfo?.hasData ? 'text-white/80' : 'text-white/20'}>
                {dayInfo?.day}
              </span>
              {dayInfo?.hasData && (
                <span className="text-[10px]" style={{ color: sage }}>
                  {dayInfo.completion}%
                </span>
              )}
            </button>
          ))}
        </div>
      </section>

      {/* Selected Day Details */}
      {selectedDayData && (
        <section className="glass-card p-6 animate-fade-in" data-testid="day-details-section">
          <h3 className="text-sm text-white/30 mb-6 tracking-widest uppercase">
            {new Date(selectedDate).toLocaleDateString('en-US', { 
              weekday: 'long', 
              month: 'long', 
              day: 'numeric',
              year: 'numeric'
            })}
          </h3>

          <div className="space-y-6">
            {/* Day Rating */}
            {selectedDayData.dayRating && (
              <div className="text-center p-4 rounded-xl" style={{ backgroundColor: sageBg }}>
                <p className="text-white/40 text-xs mb-2">Day Rating</p>
                <span className="text-3xl font-light" style={{ color: sage }}>
                  {selectedDayData.dayRating}/10
                </span>
              </div>
            )}

            {/* Sleep */}
            {selectedDayData.sleepQuality && (
              <div>
                <p className="text-white/40 text-xs mb-2 tracking-widest uppercase">Sleep</p>
                <p className="text-white/70">
                  {selectedDayData.sleepQuality === 'great' ? 'Slept well' :
                   selectedDayData.sleepQuality === 'notbad' ? 'Could use more' :
                   selectedDayData.sleepQuality === 'bad' ? 'Rough night' : "Didn't sleep"}
                </p>
                {selectedDayData.wakeUpTime && (
                  <p className="text-white/40 text-sm">Woke up at {selectedDayData.wakeUpTime}</p>
                )}
              </div>
            )}

            {/* Gratitude */}
            {selectedDayData.gratitude && (
              <div>
                <p className="text-white/40 text-xs mb-2 tracking-widest uppercase">Gratitude</p>
                <p className="text-white/70 italic">"{selectedDayData.gratitude}"</p>
              </div>
            )}

            {/* Intention */}
            {selectedDayData.whatWillMakeTodayGreat && (
              <div>
                <p className="text-white/40 text-xs mb-2 tracking-widest uppercase">Intention</p>
                <p className="text-white/70">"{selectedDayData.whatWillMakeTodayGreat}"</p>
              </div>
            )}

            {/* Non-negotiables */}
            {selectedDayData.nonNegotiables?.length > 0 && (
              <div>
                <p className="text-white/40 text-xs mb-2 tracking-widest uppercase">Non-negotiables</p>
                <div className="space-y-2">
                  {selectedDayData.nonNegotiables.map((task, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded-full flex items-center justify-center text-xs"
                        style={{
                          backgroundColor: task.completed ? sage : 'transparent',
                          border: task.completed ? 'none' : '1px solid rgba(255,255,255,0.2)'
                        }}
                      >
                        {task.completed && '✓'}
                      </div>
                      <span className={task.completed ? 'text-white/40 line-through' : 'text-white/70'}>
                        {task.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Feeling */}
            {selectedDayData.feeling && (
              <div>
                <p className="text-white/40 text-xs mb-2 tracking-widest uppercase">Morning Energy</p>
                <p className="text-white/70">{selectedDayData.feeling}/10</p>
                {selectedDayData.feelingWhy && (
                  <p className="text-white/40 text-sm mt-1">"{selectedDayData.feelingWhy}"</p>
                )}
              </div>
            )}

            {/* Completed Habits Summary */}
            <div>
              <p className="text-white/40 text-xs mb-2 tracking-widest uppercase">Habits Completed</p>
              <div className="flex flex-wrap gap-2">
                {selectedDayData.gym && <span className="px-3 py-1 rounded-full text-xs" style={{ backgroundColor: sageBg, color: sage }}>Gym</span>}
                {selectedDayData.nature && <span className="px-3 py-1 rounded-full text-xs" style={{ backgroundColor: sageBg, color: sage }}>Nature</span>}
                {selectedDayData.practicedSkill && <span className="px-3 py-1 rounded-full text-xs" style={{ backgroundColor: sageBg, color: sage }}>Skill</span>}
                {selectedDayData.moneyMove && <span className="px-3 py-1 rounded-full text-xs" style={{ backgroundColor: sageBg, color: sage }}>Money Move</span>}
                {selectedDayData.decluttered && <span className="px-3 py-1 rounded-full text-xs" style={{ backgroundColor: sageBg, color: sage }}>Decluttered</span>}
                {selectedDayData.connectedWithSomeone && <span className="px-3 py-1 rounded-full text-xs" style={{ backgroundColor: sageBg, color: sage }}>Connected</span>}
              </div>
            </div>

            {/* Water & Reading */}
            <div className="grid grid-cols-2 gap-4">
              {selectedDayData.waterOunces > 0 && (
                <div className="p-3 rounded-xl bg-white/[0.02]">
                  <p className="text-white/40 text-xs">Water</p>
                  <p className="text-white/70">{selectedDayData.waterOunces} oz</p>
                </div>
              )}
              {selectedDayData.readPages > 0 && (
                <div className="p-3 rounded-xl bg-white/[0.02]">
                  <p className="text-white/40 text-xs">Reading</p>
                  <p className="text-white/70">{selectedDayData.readPages} pages</p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Empty State */}
      {!insights && (
        <section className="text-center py-12">
          <Calendar className="w-12 h-12 mx-auto mb-4 text-white/20" />
          <p className="text-white/40 mb-2">No entries yet</p>
          <p className="text-white/20 text-sm">Start tracking your habits to see your journey here</p>
        </section>
      )}
    </div>
  );
};

export default Reflections;
